import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react";
import axios, {baseUrl} from "../components/Univ.js";
import {jwtDecode} from "jwt-decode";
import {useLocation, useNavigate} from "react-router-dom";
const AuthContext = createContext(null);
export default AuthContext;
/* =========================================================
   TOKEN HELPERS
========================================================= */
const getStoredTokens = () => {
    try {
        const stored = localStorage.getItem("authTokens");
        if (!stored) return null;
        const tokens = JSON.parse(stored);
        return tokens?.access ? tokens : null;
    } catch (error) {
        console.error("Error reading authTokens:", error);
        return null;
    }
};
const decodeToken = (accessToken) => {
    try {
        return accessToken ? jwtDecode(accessToken) : null;
    } catch (error) {
        console.error("Error decoding JWT:", error);
        return null;
    }
};
const getHospitalCode = () => localStorage.getItem("hospital_code") || localStorage.getItem("tenant") || "";
/* =========================================================
   USER VALUE HELPERS
========================================================= */
const normalizeGroupValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "string") {
                    return item.trim();
                }
                if (item && typeof item === "object") {
                    return String(item.name ?? item.group_name ?? item.title ?? "").trim();
                }
                return "";
            })
            .filter(Boolean);
    }
    if (typeof value === "object") {
        return String(value.name ?? value.group_name ?? value.title ?? "").trim();
    }
    return String(value).trim();
};
const getUsername = (decoded = {}, responseData = {}) => String(decoded?.username || decoded?.user_name || decoded?.preferred_username || responseData?.user?.username || responseData?.username || responseData?.user?.user_login || responseData?.user_login || responseData?.name || localStorage.getItem("username") || localStorage.getItem("user_login") || "").trim();
const getUserId = (decoded = {}, responseData = {}) => String(responseData?.user_id ?? responseData?.user?.user_id ?? responseData?.user?.id ?? decoded?.user_id ?? decoded?.id ?? decoded?.sub ?? localStorage.getItem("user_id") ?? "");
const getUserGroup = (decoded = {}, responseData = {}) => {
    const candidates = [responseData?.userGroup, responseData?.user_group, responseData?.group, responseData?.groups, responseData?.user?.userGroup, responseData?.user?.user_group, responseData?.user?.group, responseData?.user?.groups, decoded?.userGroup, decoded?.user_group, decoded?.group, decoded?.groups, decoded?.role, localStorage.getItem("userGroup"), localStorage.getItem("user_group"), localStorage.getItem("group"),];
    for (const candidate of candidates) {
        const group = normalizeGroupValue(candidate);
        if (Array.isArray(group) && group.length > 0) {
            return group;
        }
        if (group) {
            return group;
        }
    }
    return [];
};
const getUserLocation = (decoded = {}, responseData = {}) => {
    const value = responseData?.userLocation ?? responseData?.user_location ?? responseData?.location_id ?? responseData?.location ?? responseData?.user?.userLocation ?? responseData?.user?.location ?? decoded?.userLocation ?? decoded?.user_location ?? decoded?.location_id ?? decoded?.location ?? localStorage.getItem("userLocation") ?? "";
    if (value && typeof value === "object") {
        return String(value.id ?? value.location_id ?? value.pk ?? "");
    }
    return value === undefined || value === null ? "" : String(value);
};
const buildUser = (tokens, responseData = {}) => {
    const decoded = decodeToken(tokens?.access);
    if (!decoded) return null;
    const username = getUsername(decoded, responseData);
    const userId = getUserId(decoded, responseData);
    const userGroups = getUserGroup(decoded, responseData);
    const userGroup = Array.isArray(userGroups) ? userGroups : userGroups ? [userGroups] : [];
    const userLocation = getUserLocation(decoded, responseData);
    return {
        ...decoded,
        id: decoded.id ?? userId,
        user_id: decoded.user_id ?? userId,
        username,
        userGroup,
        userGroups: userGroup,
        userLocation,
    };
};
/* =========================================================
   MENU AND ROUTE HELPERS

   Landing route is obtained from the database-driven menu.
   No role-to-route mapping is hardcoded here.
========================================================= */
const normalizeRoute = (route) => {
    if (typeof route !== "string") return "";
    const value = route.trim();
    if (!value.startsWith("/") || value.startsWith("//") || value === "/") {
        return "";
    }
    return value;
};
const getMenuItems = (responseData) => {
    if (Array.isArray(responseData)) {
        return responseData;
    }
    if (Array.isArray(responseData?.results)) {
        return responseData.results;
    }
    if (Array.isArray(responseData?.menu)) {
        return responseData.menu;
    }
    if (Array.isArray(responseData?.data)) {
        return responseData.data;
    }
    return [];
};
const getMenuItemRoute = (item) => normalizeRoute(item?.link ?? item?.route ?? item?.path ?? item?.url ?? item?.menu_link);
const isTrueFlag = (value) => value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "yes";
/*
 * If the API returns group information with each menu item,
 * use it to prefer a menu entry belonging to the logged-in
 * user's group. If the API already filters the menu by group,
 * the returned entries are used directly.
 */
const itemMatchesUserGroup = (item, userGroup) => {
    if (!userGroup) return false;
    const itemGroup = item?.userGroup ?? item?.user_group ?? item?.group ?? item?.group_name;
    if (!itemGroup) return false;
    const values = Array.isArray(itemGroup) ? itemGroup : [itemGroup];
    return values.some((value) => {
        const name = normalizeGroupValue(value);
        return (name.toLowerCase() === userGroup.toLowerCase());
    });
};
const chooseLandingRoute = (menuItems, userGroup, permissionData = {}) => {
    return "/admin-menu/";
};
/* =========================================================
   AUTH PROVIDER
========================================================= */
export const AuthProvider = ({children}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [authTokens, setAuthTokens] = useState(() => getStoredTokens());
    const [user, setUser] = useState(() => buildUser(getStoredTokens()));
    const [loading, setLoading] = useState(true);
    const [routePermissions, setRoutePermissions] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("routePermissions") || "[]");
        } catch {
            return [];
        }
    });
    const [landingRoute, setLandingRoute] = useState(() => localStorage.getItem("landingRoute") || "");
    const [routePermissionsLoading, setRoutePermissionsLoading] = useState(false);
    const [routePermissionsError, setRoutePermissionsError] = useState("");
    const [authError, setAuthError] = useState("");
    const loginInProgress = useRef(false);
    const permissionsRequestKey = useRef("");
    const restoringSession = useRef(false);
    const hospitalCode = getHospitalCode();
    const isAuthenticated = Boolean(authTokens?.access && user);
    /* =====================================================
       AUTHENTICATED REQUEST HEADERS
    ===================================================== */
    const getHeaders = useCallback((tokens, hospital) => {
        const headers = {};
        if (tokens?.access) {
            headers.Authorization = `Bearer ${tokens.access}`;
        }
        if (hospital) {
            headers["X-Hospital-Code"] = hospital;
        }
        return headers;
    }, []);
    /* =====================================================
       STORE USER DETAILS

       Important values retained from the original context:
       username, user_login, authTokens, user_id,
       userLocation, userGroup, hospital_code and tenant.
    ===================================================== */
    const storeUserDetails = useCallback((tokens, responseData = {}) => {
        const nextUser = buildUser(tokens, responseData);
        if (!nextUser) {
            throw new Error("Unable to decode the authenticated user's access token.");
        }
        if (nextUser.username) {
            localStorage.setItem("username", nextUser.username);
            // Keep the actual username, not boolean true.
            localStorage.setItem("user_login", nextUser.username);
        }
        if (nextUser.user_id) {
            localStorage.setItem("user_id", String(nextUser.user_id));
        }
        if (nextUser.userGroup?.length) {
            localStorage.setItem("userGroup", JSON.stringify(nextUser.userGroup));
            localStorage.setItem("user_group", JSON.stringify(nextUser.userGroup));
        }
        if (nextUser.userLocation !== "") {
            localStorage.setItem("userLocation", String(nextUser.userLocation));
        }
        localStorage.setItem("authTokens", JSON.stringify(tokens));
        return nextUser;
    }, []);
    /* =====================================================
       CLEAR ROUTE PERMISSIONS
    ===================================================== */
    const clearRoutePermissions = useCallback(() => {
        setRoutePermissions([]);
        setLandingRoute("");
        setRoutePermissionsError("");
        localStorage.removeItem("routePermissions");
        localStorage.removeItem("landingRoute");
        permissionsRequestKey.current = "";
    }, []);
    /* =====================================================
       CLEAR SESSION
    ===================================================== */
    const clearStoredSession = useCallback(() => {
        ["authTokens", "username", "user_login", "user_id", "userGroup", "user_group", "group", "userLocation", "token", "routePermissions", "landingRoute",].forEach((key) => {
            localStorage.removeItem(key);
        });
        // Keep hospital_code and tenant so the selected
        // hospital can remain available on the login screen.
    }, []);
    /* =====================================================
       FETCH USER-GROUP ROUTES AND DATABASE MENU
    ===================================================== */
    const fetchRoutePermissions = useCallback(async (tokens, hospital, currentUser) => {
        const headers = getHeaders(tokens, hospital);
        /*
         * Fetch route permissions assigned by the backend.
         */
        const permissionResponse = await axios.get(`${baseUrl}route-permissions/`, {headers});
        const permissionData = permissionResponse.data || {};
        const rawRoutes = Array.isArray(permissionData) ? permissionData : Array.isArray(permissionData.routes) ? permissionData.routes : [];
        const routes = [...new Set(rawRoutes
            .map((item) => typeof item === "string" ? item.trim() : String(item?.route ?? item?.link ?? item?.path ?? "").trim())
            .filter((route) => route.startsWith("/") && !route.startsWith("//"))),];
        /*
         * Fetch the actual menu assigned to this user.
         * The API should filter its response according
         * to the authenticated user's group/permissions.
         */
        const menuResponse = await axios.get(`${baseUrl}menu/`, {headers});
        const menuItems = getMenuItems(menuResponse.data);
        const userGroup = currentUser?.userGroup || getUserGroup(decodeToken(tokens?.access));
        const selectedLandingRoute = chooseLandingRoute(menuItems, userGroup, permissionData);
        if (!selectedLandingRoute) {
            throw new Error("No valid landing route was returned by the database menu. " + "Check the menu/ API response for this user's group.");
        }
        console.log("Authenticated username:", currentUser?.username);
        console.log("Authenticated user group:", userGroup);
        console.log("Database menu returned:", menuItems.map((item) => ({
            title: item.title,
            link: item.link ?? item.route ?? item.path,
            group: item.userGroup ?? item.user_group ?? item.group ?? item.group_name,
        })));
        console.log("Selected dynamic landing route:", selectedLandingRoute);
        return {
            routes, landingRoute: selectedLandingRoute, menuItems,
        };
    }, [getHeaders]);
    /* =====================================================
       LOAD AND STORE ROUTE PERMISSIONS
    ===================================================== */
    const loadRoutePermissions = useCallback(async (tokensOverride = null, hospitalOverride = null, userOverride = null) => {
        const tokens = tokensOverride || authTokens;
        const hospital = hospitalOverride || getHospitalCode();
        const currentUser = userOverride || user || buildUser(tokens);
        if (!tokens?.access) {
            clearRoutePermissions();
            return null;
        }
        setRoutePermissionsLoading(true);
        setRoutePermissionsError("");
        try {
            const result = await fetchRoutePermissions(tokens, hospital, currentUser);
            setRoutePermissions(result.routes);
            setLandingRoute(result.landingRoute);
            localStorage.setItem("routePermissions", JSON.stringify(result.routes));
            localStorage.setItem("landingRoute", result.landingRoute);
            const decoded = decodeToken(tokens.access);
            permissionsRequestKey.current = `${getUserId(decoded)}|${hospital}`;
            return result;
        } catch (error) {
            const message = error.response?.data?.detail || error.response?.data?.error || error.message || "Unable to load route permissions.";
            setRoutePermissionsError(message);
            console.error("Route permission loading failed:", error.response?.status, error.response?.data || error.message);
            throw error;
        } finally {
            setRoutePermissionsLoading(false);
        }
    }, [authTokens, user, clearRoutePermissions, fetchRoutePermissions,]);
    /* =====================================================
       LOGIN

       Supports both:
       loginUser(hospitalCode, username, password)

       and the original form usage:
       loginUser(event)
    ===================================================== */
    const loginUser = useCallback(async (hospitalOrEvent, usernameArgument, passwordArgument) => {
        if (loginInProgress.current) return;
        loginInProgress.current = true;
        setLoading(true);
        setAuthError("");
        try {
            let hospitalInput;
            let usernameInput;
            let passwordInput;
            if (hospitalOrEvent && typeof hospitalOrEvent.preventDefault === "function") {
                const event = hospitalOrEvent;
                event.preventDefault();
                hospitalInput = event.target.hospital_code?.value;
                usernameInput = event.target.username?.value;
                passwordInput = event.target.password?.value;
            } else {
                hospitalInput = hospitalOrEvent;
                usernameInput = usernameArgument;
                passwordInput = passwordArgument;
            }
            const hospital = String(hospitalInput || "").trim();
            const username = String(usernameInput || "").trim();
            if (!hospital || !username || !passwordInput) {
                throw new Error("Hospital code, username and password are required.");
            }
            /*
             * Preserve the hospital-specific login request.
             */
            const response = await axios.post(`${baseUrl}token/`, {
                hospital_code: hospital, username, password: passwordInput,
            }, {
                headers: {
                    "X-Hospital-Code": hospital,
                },
            });
            const tokens = response.data;
            if (!tokens?.access) {
                throw new Error("Login response did not include an access token.");
            }
            /*
             * Save the tenant before making authenticated
             * menu and route-permission requests.
             */
            localStorage.setItem("hospital_code", hospital);
            localStorage.setItem("tenant", hospital);
            /*
             * Build the user from both the login response
             * and the decoded JWT.
             */
            const decoded = decodeToken(tokens.access);
            const loginResponse = {
                ...response.data,
                username: decoded?.username || decoded?.user_name || decoded?.preferred_username || response.data?.user?.username || response.data?.username || username,
            };
            const nextUser = storeUserDetails(tokens, loginResponse);

            setAuthTokens(tokens);
            setUser(nextUser);
            clearRoutePermissions();
            /*
             * Landing page is chosen from the user's
             * database menu, not a hardcoded group map.
             */
            const result = await loadRoutePermissions(tokens, hospital, nextUser);
            navigate(result.landingRoute, {replace: true});
            return {
                success: true, user: nextUser, landingRoute: result.landingRoute,
            };
        } catch (error) {
            const message = error.response?.data?.detail || error.response?.data?.error || error.response?.data?.non_field_errors?.[0] || error.message || "Login failed.";
            setAuthError(message);
            console.error("Login error:", error.response?.status, error.response?.data || error.message);
            /*
             * The login component can display this message.
             * Preserve its existing catch/error handling.
             */
            throw error;
        } finally {
            setLoading(false);
            loginInProgress.current = false;
        }
    }, [storeUserDetails, clearRoutePermissions, loadRoutePermissions, navigate,]);
    /* =====================================================
       LOGOUT
    ===================================================== */
    const logoutUser = useCallback(() => {
        setAuthTokens(null);
        setUser(null);
        setAuthError("");
        clearRoutePermissions();
        clearStoredSession();
        navigate("/user-login", {replace: true});
    }, [clearRoutePermissions, clearStoredSession, navigate,]);
    /* =====================================================
       RESTORE AND REFRESH SAVED SESSION
    ===================================================== */
    const refreshSessionTokens = useCallback(async (tokensToRefresh) => {
        if (!tokensToRefresh?.refresh) {
            throw new Error("No refresh token is available.");
        }
        const hospital = getHospitalCode();
        const response = await axios.post(`${baseUrl}token/refresh/`, {
            refresh: tokensToRefresh.refresh,
        }, {
            headers: hospital ? {"X-Hospital-Code": hospital} : {},
        });
        if (!response.data?.access) {
            throw new Error("Token refresh response did not include an access token.");
        }
        /*
         * Some refresh endpoints return only a new access
         * token. Preserve the existing refresh token then.
         */
        const updatedTokens = {
            ...tokensToRefresh, ...response.data, refresh: response.data?.refresh || tokensToRefresh.refresh,
        };
        const decoded = decodeToken(updatedTokens.access);
        if (!decoded) {
            throw new Error("The refreshed access token is invalid.");
        }
        /*
         * Retain stored identity values if the refreshed
         * JWT does not contain every custom claim.
         */
        const updatedUser = storeUserDetails(updatedTokens, {
            username: decoded.username || localStorage.getItem("username"),
            user_id: decoded.user_id ?? localStorage.getItem("user_id"),
            userGroup: decoded.userGroup ?? decoded.user_group ?? decoded.group ?? decoded.groups ?? localStorage.getItem("userGroup"),
            userLocation: decoded.userLocation ?? decoded.user_location ?? decoded.location_id ?? decoded.location ?? localStorage.getItem("userLocation"),
        });
        setAuthTokens(updatedTokens);
        setUser(updatedUser);
        /*
         * Keep tenant synced if the refreshed JWT includes
         * a tenant value; otherwise preserve the selected
         * hospital code already stored.
         */
        if (decoded.tenant) {
            localStorage.setItem("tenant", String(decoded.tenant));
        }
        return {
            tokens: updatedTokens, user: updatedUser,
        };
    }, [storeUserDetails]);
    /* =====================================================
       INITIAL SESSION RESTORATION
    ===================================================== */
    useEffect(() => {
        let cancelled = false;
        const restoreSession = async () => {
            if (restoringSession.current) return;
            restoringSession.current = true;
            const savedTokens = getStoredTokens();
            if (!savedTokens?.refresh) {
                if (!cancelled) {
                    setAuthTokens(null);
                    setUser(null);
                    setLoading(false);
                }
                restoringSession.current = false;
                return;
            }
            /*
             * Restore the existing user immediately so the
             * application can identify the saved session.
             */
            const savedUser = buildUser(savedTokens);
            if (!cancelled) {
                setAuthTokens(savedTokens);
                setUser(savedUser);
            }
            try {
                const result = await refreshSessionTokens(savedTokens);
                if (cancelled) return;
                setAuthTokens(result.tokens);
                setUser(result.user);
            } catch (error) {
                if (cancelled) return;
                const status = error.response?.status;
                console.error("Session restoration/refresh failed:", status || "network error", error.response?.data || error.message);
                if (status === 400 || status === 401) {
                    clearStoredSession();
                    clearRoutePermissions();
                    setAuthTokens(null);
                    setUser(null);
                } else {
                    /*
                     * Do not erase the session because of a
                     * temporary network/server failure.
                     */
                    const currentTokens = getStoredTokens();
                    setAuthTokens(currentTokens);
                    setUser(buildUser(currentTokens));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
                restoringSession.current = false;
            }
        };
        restoreSession();
        return () => {
            cancelled = true;
        };
    }, [refreshSessionTokens, clearStoredSession, clearRoutePermissions,]);
    /* =====================================================
       LOAD PERMISSIONS FOR A RESTORED SESSION
    ===================================================== */
    useEffect(() => {
        if (loading || !authTokens?.access || !user) {
            return;
        }
        const hospital = getHospitalCode();
        const decoded = decodeToken(authTokens.access);
        const userId = getUserId(decoded);
        if (!hospital || !userId) return;
        const requestKey = `${userId}|${hospital}`;
        if (permissionsRequestKey.current === requestKey) {
            return;
        }
        let cancelled = false;
        const restorePermissions = async () => {
            try {
                const result = await loadRoutePermissions(authTokens, hospital, user);
                if (cancelled || !result?.landingRoute) {
                    return;
                }
                /*
                 * Redirect only when on the login/root route.
                 * Do not force the user back to the landing
                 * page when refreshing another page.
                 */
                if (location.pathname === "/" || location.pathname === "/user-login") {
                    navigate(result.landingRoute, {replace: true});
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Could not restore route permissions:", error.message);
                }
            }
        };
        restorePermissions();
        return () => {
            cancelled = true;
        };
    }, [loading, authTokens, user, location.pathname, loadRoutePermissions, navigate,]);
    /* =====================================================
       AUTOMATIC TOKEN REFRESH EVERY FOUR MINUTES
    ===================================================== */
    useEffect(() => {
        if (loading || !authTokens?.refresh || !user) {
            return;
        }
        let cancelled = false;
        let refreshInProgress = false;
        const refreshAutomatically = async () => {
            if (refreshInProgress) return;
            refreshInProgress = true;
            try {
                const result = await refreshSessionTokens(authTokens);
                if (cancelled) return;
                setAuthTokens(result.tokens);
                setUser(result.user);
            } catch (error) {
                if (cancelled) return;
                const status = error.response?.status;
                console.error("Automatic token refresh failed:", status || "network error", error.response?.data || error.message);
                /*
                 * Only clear the session when the server says
                 * the refresh token is invalid/unauthorized.
                 * Preserve it for temporary network errors.
                 */
                if (status === 400 || status === 401) {
                    logoutUser();
                }
            } finally {
                refreshInProgress = false;
            }
        };
        const interval = setInterval(refreshAutomatically, 4 * 60 * 1000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [loading, authTokens, user, refreshSessionTokens, logoutUser,]);
    /* =====================================================
       CONTEXT VALUES
    ===================================================== */
    const contextValue = useMemo(() => ({
        user,
        authTokens,
        isAuthenticated,
        loading,
        loginUser,
        logoutUser, // Identity values used by Header and other pages.
        username: user?.username || localStorage.getItem("username") || localStorage.getItem("user_login") || "",
        userId: user?.user_id ?? user?.id ?? localStorage.getItem("user_id") ?? "",
        userGroup: user?.userGroup || localStorage.getItem("userGroup") || localStorage.getItem("user_group") || "",
        userLocation: user?.userLocation || localStorage.getItem("userLocation") || "",
        hospitalCode: getHospitalCode(), // Database-driven menu/route values.
        routePermissions,
        landingRoute,
        routePermissionsLoading,
        routePermissionsError,
        authError,
        reloadRoutePermissions: () => loadRoutePermissions(),
    }), [user, authTokens, isAuthenticated, loading, loginUser, logoutUser, routePermissions, landingRoute, routePermissionsLoading, routePermissionsError, authError, loadRoutePermissions,]);
    /*
     * Prevent protected components from rendering before
     * initial session restoration has finished.
     */
    if (loading) return null;
    return (<AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>);
};
/* =========================================================
   USE AUTH HOOK
========================================================= */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used inside AuthProvider.");
    }
    return context;
};
export {AuthContext};